import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import { isValid } from 'date-fns';
import React from 'react';
import { DateTimeRangePicker } from '../DateTimeRangePicker';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { convertDateToValue, convertValueToDate } from '../WidgetFormDatePicker/Utils/date';
import { type } from './types';
const WidgetFormDateTimePicker = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const data = (_a = props.getData()) !== null && _a !== void 0 ? _a : {};
    const value = (_b = props.internalData.value) !== null && _b !== void 0 ? _b : {
        fromDate: convertValueToDate(data === null || data === void 0 ? void 0 : data.from),
        toDate: convertValueToDate(data === null || data === void 0 ? void 0 : data.to),
    };
    const { t } = useCommonFormUiTranslation();
    const error = getVisibleError(props);
    const tabIndex = props.editMode ? -1 : undefined;
    const formLabelSuffix = getFormLabelSuffix(props);
    const fromDateLabel = (_d = (_c = props.config.fromDateLabel) !== null && _c !== void 0 ? _c : props.config.label) !== null && _d !== void 0 ? _d : t('widget.widgetDatePicker.fromDate');
    const fromTimeLabel = (_f = (_e = props.config.fromTimeLabel) !== null && _e !== void 0 ? _e : props.config.label) !== null && _f !== void 0 ? _f : t('widget.widgetDatePicker.fromTime');
    const toDateLabel = (_h = (_g = props.config.toDateLabel) !== null && _g !== void 0 ? _g : props.config.label) !== null && _h !== void 0 ? _h : t('widget.widgetDatePicker.toDate');
    const toTimeLabel = (_k = (_j = props.config.toTimeLabel) !== null && _j !== void 0 ? _j : props.config.label) !== null && _k !== void 0 ? _k : t('widget.widgetDatePicker.toTime');
    const readOnly = props.config.readOnly || ((_l = props.rootParams.formParams) === null || _l === void 0 ? void 0 : _l.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_m = props.config.validate) === null || _m === void 0 ? void 0 : _m.required, error: !!error, disabled: props.config.disabled },
        React.createElement(DateTimeRangePicker, { fromDateLabel: formLabelSuffix ? fromDateLabel + formLabelSuffix : fromDateLabel, fromTimeLabel: formLabelSuffix ? fromTimeLabel + formLabelSuffix : fromTimeLabel, toDateLabel: formLabelSuffix ? toDateLabel + formLabelSuffix : toDateLabel, toTimeLabel: formLabelSuffix ? toTimeLabel + formLabelSuffix : toTimeLabel, separatorLabel: (_p = (_o = props.config.separatorLabel) !== null && _o !== void 0 ? _o : props.config.toLabel) !== null && _p !== void 0 ? _p : t('common.to'), minDate: convertValueToDate((_q = props.config.validate) === null || _q === void 0 ? void 0 : _q.minDate), maxDate: convertValueToDate((_r = props.config.validate) === null || _r === void 0 ? void 0 : _r.maxDate), timeFormat: props.config.timeFormat, timeMinuteStep: props.config.timeMinuteStep, required: (_s = props.config.validate) === null || _s === void 0 ? void 0 : _s.required, disabled: props.config.disabled, readOnly: readOnly, fullWidth: true, error: !!error, fromDateTabIndex: tabIndex, fromTimeTabIndex: tabIndex, toDateTabIndex: tabIndex, toTimeTabIndex: tabIndex, value: value, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                props.setData({
                    from: convertDateToValue(newValue.fromDate, props.config.isoString),
                    to: convertDateToValue(newValue.toDate, props.config.isoString),
                });
                props.internalData.value = newValue;
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, autoFocus: (_t = (!props.editMode && props.config.autoFocus)) !== null && _t !== void 0 ? _t : false, ariaDescribedBy: ariaDescribedBy }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormDateEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetDatePicker.dateTimeRangePicker') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            onLoad: (data) => {
                if (!data.timeFormat) {
                    data.timeFormat = '24h';
                }
                if (data.timeMinuteStep == null) {
                    data.timeMinuteStep = 30;
                }
                return data;
            },
            onSubmit: (data) => {
                if (!data.toLabel) {
                    delete data.toLabel;
                }
                return data;
            },
            children: [
                {
                    type: 'form.text',
                    key: 'separatorLabel',
                    label: t('widget.widgetTabs.separatorLabel'),
                },
                {
                    type: 'form.checkbox',
                    key: 'isoString',
                    label: t('widget.widgetDatePicker.storeAsIsoString'),
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            children: [
                {
                    type: 'form.dateTimePicker',
                    key: 'validate.minDate',
                    label: t('widget.widgetDatePicker.minDate'),
                },
                {
                    type: 'form.text',
                    key: 'validate.minDateMessage',
                    label: t('widget.widgetDatePicker.minDateMessage'),
                    emptyAsNull: true,
                },
                {
                    type: 'form.dateTimePicker',
                    key: 'validate.maxDate',
                    label: t('widget.widgetDatePicker.maxDate'),
                },
                {
                    type: 'form.text',
                    key: 'validate.maxDateMessage',
                    label: t('widget.widgetDatePicker.maxDateMessage'),
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDateTimePicker, Object.assign({}, props))));
};
export const widgetFormDateTimeRangePicker = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        if (isSkipValidate(props)) {
            return;
        }
        if (((_c = (_b = (_a = props.internalData.value) === null || _a === void 0 ? void 0 : _a.fromValue) === null || _b === void 0 ? void 0 : _b.dateValue) === null || _c === void 0 ? void 0 : _c.invalidDate) != null) {
            return 'Invalid from date';
        }
        if (((_f = (_e = (_d = props.internalData.value) === null || _d === void 0 ? void 0 : _d.toValue) === null || _e === void 0 ? void 0 : _e.dateValue) === null || _f === void 0 ? void 0 : _f.invalidDate) != null) {
            return 'Invalid to date';
        }
        const value = (_g = props.getData()) !== null && _g !== void 0 ? _g : {};
        const minDate = convertValueToDate((_h = props.config.validate) === null || _h === void 0 ? void 0 : _h.minDate);
        const maxDate = convertValueToDate((_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.maxDate);
        let dateFrom = undefined;
        if (value.from != null) {
            dateFrom = convertValueToDate(value.from);
            if (dateFrom && isValid(dateFrom)) {
                if (minDate && isValid(minDate) && dateFrom < minDate) {
                    return (_l = (_k = props.config.validate) === null || _k === void 0 ? void 0 : _k.minDateMessage) !== null && _l !== void 0 ? _l : 'From date cannot be before ' + minDate.toLocaleDateString(); // TODO format
                }
                if (maxDate && isValid(maxDate) && dateFrom > maxDate) {
                    return (_o = (_m = props.config.validate) === null || _m === void 0 ? void 0 : _m.minDateMessage) !== null && _o !== void 0 ? _o : 'From date cannot be after ' + maxDate.toLocaleDateString(); // TODO format
                }
            }
            else {
                return 'Invalid from date';
            }
        }
        let dateTo = undefined;
        if (value.to != null) {
            dateTo = convertValueToDate(value.to);
            if (dateTo && isValid(dateTo)) {
                if (minDate && isValid(minDate) && dateTo < minDate) {
                    return (_q = (_p = props.config.validate) === null || _p === void 0 ? void 0 : _p.maxDateMessage) !== null && _q !== void 0 ? _q : 'To date cannot be before ' + minDate.toLocaleDateString(); // TODO format
                }
                if (maxDate && isValid(maxDate) && dateTo > maxDate) {
                    return (_s = (_r = props.config.validate) === null || _r === void 0 ? void 0 : _r.maxDateMessage) !== null && _s !== void 0 ? _s : 'To date cannot be after ' + maxDate.toLocaleDateString(); // TODO format
                }
            }
            else {
                return 'Invalid to date';
            }
        }
        if (((_t = props.config.validate) === null || _t === void 0 ? void 0 : _t.required) && (value.from == null || value.to == null)) {
            if (props.config.fromTimeLabel && props.config.fromDateLabel && props.config.toDateLabel && props.config.toTimeLabel) {
                const invalidFields = [];
                if (!value.from) {
                    invalidFields.push(props.config.fromDateLabel, props.config.fromTimeLabel);
                }
                if (!value.to) {
                    invalidFields.push(props.config.toDateLabel, props.config.toTimeLabel);
                }
                const invalidUniqueFields = invalidFields.filter((value, index) => invalidFields.indexOf(value) === index);
                const formattedInvalidFields = invalidUniqueFields.reduce((previousValue, currentValue, currentIndex) => {
                    return previousValue + (currentIndex === invalidUniqueFields.length - 1 ? ' and ' : ', ') + currentValue;
                });
                const errorCase = invalidUniqueFields.length === 1 ? ' is required' : ' are required';
                return formattedInvalidFields + errorCase;
            }
            else {
                return (_u = props.config.validate.requiredMessage) !== null && _u !== void 0 ? _u : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (value.from && value.to && value.from === value.to) {
            return 'Start and end times are the same';
        }
        if (dateFrom && dateTo && dateFrom > dateTo) {
            return 'From date cannot be after to date';
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a;
        const value = (_a = props.getData()) !== null && _a !== void 0 ? _a : {};
        if (value != null && (value.from != null || value.to != null)) {
            const dateFrom = convertValueToDate(value.from);
            const dateTo = convertValueToDate(value.to);
            const stringFrom = dateFrom ? dateFrom.toLocaleDateString() : '...'; // TODO format
            const stringTo = dateTo ? dateTo.toLocaleDateString() : '...'; // TODO format
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), stringFrom + ' - ' + stringTo);
        }
        return [];
    },
    component: WidgetFormDateTimePicker,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDateEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'dateTimePicker', label: 'Date Time Range Picker' }),
};
